import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import dayjs from 'dayjs';
import moment from 'moment';
import { BooleanField, ChipField, Datagrid, DateField, DateInput, Filter, FunctionField, NumberField, ReferenceArrayField, ReferenceArrayInput, ReferenceField, ReferenceInput, SelectArrayInput, SelectInput, SingleFieldList, TextField, useGetIdentity, useListContext } from 'react-admin';
import { CustomizableDatagrid, ListPagination, ReportList } from '../../components';
import { useRowClick } from '../../hooks/useRowClick';
import { UserRole } from '../../providers/authProvider';

const useStyles = makeStyles(theme => ({
  title: {
    margin: '10px 0 10px 0',
    textAlign: 'center',
    fontWeight: 'bold',
    paddingTop: 30
  },
  headerCell: {
    backgroundColor: '#E5E5E5',
  },
}));

const FillinList = () => {
  const classes = useStyles();
  const props = useListContext();
  const { identity } = useGetIdentity();
  const { handleRowClick } = useRowClick();

  let data = [];
  let summaryData = [];

  if (!props.data) { return <></>; }

  if (props.data) {
    const fillinInfo = props.data[0];
    if (fillinInfo) {
      data = fillinInfo.data;
      summaryData = [fillinInfo.summaryData];
    }
  }

  return (
    <>
      <Typography className={classes.title}>Total</Typography>
      <Datagrid classes={{ headerCell: classes.headerCell }} bulkActionButtons={false} style={{ marginBottom: 60 }} total={1} data={summaryData}>
        <NumberField
          source="total_value_to_pay"
          label="Total Pago pela Transportadora"
          textAlign="center"
          emptyText="R$ 0,00"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
          sortable={false}
        />
        <NumberField
          source="total_tax_amount"
          label="Total de Taxa"
          textAlign="center"
          emptyText="R$ 0,00"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
          sortable={false}
        />
        <NumberField
          source="average_tax_rate"
          label="Taxa Média (%)"
          textAlign="center"
          emptyText="0%"
          options={{
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
          sortable={false}
        />
        <NumberField
          source="total_value_to_receive"
          label="Total Recebido pelo Posto"
          textAlign="center"
          emptyText="R$ 0,00"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
          sortable={false}
        />
        <TextField
          source="total_fillins"
          textAlign="center"
          emptyText="--"
          label="Total de abastecimentos"
          sortable={false}
        />
      </Datagrid>
      <Typography className={classes.title}>Abastecimentos - Relatório de Margem</Typography>
      <CustomizableDatagrid
        classes={{ headerCell: classes.headerCell }}
        resource="fillins"
        data={data}
        rowClick={(id, resource, record) => handleRowClick('show', id, resource, record)}
        defaultColumns={['stationId', 'subCompanyId', 'chainId', 'valueToPay', 'taxRate', 'valueToReceive', 'receivedAt', 'hasStationBilling', 'hasSubCompanyBilling']}
        bulkActionButtons={false}
      >
        <ReferenceField
          label="Posto"
          emptyText="Posto não identificado"
          source="stationId"
          basePath="stations"
          reference="stations"
        >
          <TextField source="name" />
        </ReferenceField>

        <ReferenceField source="chainId" basePath="chains" reference="chains" label="Rede" link={null}>
          <TextField emptyText="Não identificada" source="name" />
        </ReferenceField>

        <ReferenceField source="subCompanyId" basePath="sub-companies" reference="sub-companies" label="Transportadora" link={null}>
          <TextField emptyText="Não identificada" source="name" />
        </ReferenceField>

        <NumberField
          source="valueToPay"
          label="Valor Total"
          locales="pt-BR"
          textAlign="center"
          emptyText="R$ 0,00"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
        />

        <NumberField
          source="taxRate"
          label="Taxa (%)"
          textAlign="center"
          emptyText="0%"
          options={{
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
        />

        <NumberField
          source="taxAmount"
          label="Valor da Taxa"
          locales="pt-BR"
          textAlign="center"
          emptyText="R$ 0,00"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
        />

        <NumberField
          source="valueToReceive"
          label="Valor Recebido pelo Posto"
          locales="pt-BR"
          textAlign="center"
          emptyText="R$ 0,00"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
        />

        <DateField
          source="receivedAt"
          label="Data do Abastecimento"
          showTime
          locales="pt-BR"
        />

        <ReferenceField
          label="Combustível"
          emptyText="Não identificado"
          source="fuelId"
          basePath="fuels"
          reference="fuels"
          link={identity?.role === UserRole.admin && "show"}
        >
          <TextField source="name" />
        </ReferenceField>

        <BooleanField
          source="hasStationBilling"
          label="Faturado (Posto)"
          textAlign="center"
          valueLabelTrue="Sim"
          valueLabelFalse="Não"
        />

        <BooleanField
          source="hasSubCompanyBilling"
          label="Faturado (Transp.)"
          textAlign="center"
          valueLabelTrue="Sim"
          valueLabelFalse="Não"
        />
      </CustomizableDatagrid>
    </>
  );
};

const FilterTablePrint = () => {
  const classes = useStyles();
  const { filterValues } = useListContext();
  return (
    <Datagrid classes={{ headerCell: classes.headerCell }} data={[{ ...filterValues, generatedReportAt: new Date() }]} total={1} bulkActionButtons={false}>
      <DateField source="from" textAlign="center" label="De" sortable={false} locales="pt-BR" />
      <DateField source="to" textAlign="center" label="Até" sortable={false} locales="pt-BR" />

      <ReferenceField emptyText="Todas" source="chainId" textAlign="center" basePath="chains" reference="chains" link={false} label="Rede">
        <TextField source="name" />
      </ReferenceField>

      <ReferenceArrayField emptyText="Todos" source="stationIds" textAlign="center" basePath="stations" reference="stations" link={false} label="Postos">
        <SingleFieldList>
          <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>

      <ReferenceField emptyText="Todas" source="subCompanyId" textAlign="center" basePath="sub-companies" reference="sub-companies" link={false} label="Transportadora">
        <TextField source="name" />
      </ReferenceField>

      <FunctionField source="hasStationBilling" label="Faturado (Posto)" render={record => record.hasStationBilling === true ? 'Com Fatura' : record.hasStationBilling === false ? 'Sem Fatura' : 'Todos'} />

      <FunctionField source="hasSubCompanyBilling" label="Faturado (Transp.)" render={record => record.hasSubCompanyBilling === true ? 'Com Fatura' : record.hasSubCompanyBilling === false ? 'Sem Fatura' : 'Todos'} />

      <DateField source="generatedReportAt" textAlign="center" label="Gerado às" showTime sortable={false} locales="pt-BR" />
    </Datagrid>
  );
}

const MarginReport = (props) => {
  const { identity } = useGetIdentity();

  const ListFilters = () => {
    const { filterValues } = useListContext();

    return (
      <Filter>
        <DateInput
          source="from"
          label="De"
          sx={{ marginBottom: 0.5 }}
          options={{ format: 'DD/MM/YYYY' }}
          alwaysOn
          parse={(value) => moment(value).startOf('day').toISOString()}
        />
        <DateInput
          source="to"
          label="Até"
          sx={{ marginBottom: 0.5 }}
          options={{ format: 'DD/MM/YYYY' }}
          alwaysOn
          parse={(value) => moment(value).endOf('day').toISOString()}
        />

        <ReferenceInput
          label="Rede"
          source="chainId"
          reference="chains"
          sort={{ field: "name", order: "ASC" }}
          emptyText="Todas"
          alwaysOn
        >
          <SelectInput
            emptyText="Todas"
            label="Rede"
            optionText={'name'}
          />
        </ReferenceInput>

        <ReferenceArrayInput
          source="stationIds"
          reference="stations"
          sort={{ field: "name", order: "ASC" }}
          filter={{ chainId: filterValues.chainId || undefined }}
          alwaysOn
          perPage={null}
        >
          <SelectArrayInput
            label="Posto"
            optionText="name"
          />
        </ReferenceArrayInput>

        <ReferenceInput
          label="Transportadora"
          source="subCompanyId"
          reference="sub-companies"
          sort={{ field: "name", order: "ASC" }}
          emptyText="Todas"
          alwaysOn
        >
          <SelectInput
            emptyText="Todas"
            label="Transportadora"
            optionText={'name'}
          />
        </ReferenceInput>

        <SelectInput
          source="hasStationBilling"
          label="Faturado (Posto)"
          choices={[
            { name: "Sem fatura", value: false },
            { name: "Com fatura", value: true },
          ]}
          emptyText="Todos"
          optionText="name"
          optionValue="value"
          allowEmpty
          alwaysOn
        />

        <SelectInput
          source="hasSubCompanyBilling"
          label="Faturado (Transp.)"
          choices={[
            { name: "Sem fatura", value: false },
            { name: "Com fatura", value: true },
          ]}
          emptyText="Todos"
          optionText="name"
          optionValue="value"
          allowEmpty
          alwaysOn
        />
      </Filter>
    );
  };

  const fillinsExporter = async (data, fetchRelatedRecords) => {
    let fillinInfo = data[0].data;

    const relations = [
      { field: 'fuelId', resource: 'fuels' },
      { field: 'stationId', resource: 'stations' },
      { field: 'chainId', resource: 'chains' },
      { field: 'subCompanyId', resource: 'sub-companies' },
    ];

    const fetchData = await fetchRelatedRecords(relations, fillinInfo);

    const getData = (resource) => {
      return fetchData.find(({ resource: res }) => res === resource)?.results || [];
    }

    fillinInfo = fillinInfo.map(fillin => {
      const { chainId, fuelId, stationId, subCompanyId, ...fillinRest } = fillin;
      const chain = getData('chains').find(({ id }) => id === chainId);
      const station = getData('stations').find(({ id }) => id === stationId);
      const subCompany = getData('sub-companies').find(({ id }) => id === subCompanyId);
      const fuel = getData('fuels').find(({ id }) => id === fuelId);

      return {
        posto: station ? station.name : 'Não identificado',
        rede: chain ? chain.name : 'Não identificado',
        transportadora: subCompany ? subCompany.name : 'Não identificado',
        valor_total: Number(fillinRest.valueToPay).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
        taxa_percentual: fillinRest.taxRate ? `${fillinRest.taxRate}%` : '0%',
        valor_taxa: Number(fillinRest.taxAmount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
        valor_recebido_posto: Number(fillinRest.valueToReceive).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
        data_abastecimento: fillinRest.receivedAt ? dayjs(fillinRest.receivedAt).format('DD/MM/YYYY HH:mm') : '',
        combustivel: fuel ? fuel.name : 'Não identificado',
        faturado_posto: fillinRest.hasStationBilling ? 'Sim' : 'Não',
        faturado_transportadora: fillinRest.hasSubCompanyBilling ? 'Sim' : 'Não',
      };
    });

    return fillinInfo;
  }

  return (
    <ReportList
      {...props}
      basePath="fillins"
      title="Relatório de Margem"
      resource="reports/margin"
      filters={<ListFilters />}
      sort={{ field: 'receivedAt', order: 'DESC' }}
      perPage={25}
      filterDefaultValues={{
        from: moment().subtract(30, 'days').startOf('day').toISOString(),
        to: moment().endOf('day').toISOString(),
      }}
      pagination={<ListPagination />}
      bulkActionButtons={false}
      disableSyncWithLocation
      fileName='relatorio_margem'
      customExporter={fillinsExporter}
      hasSummary={false}
      titleOnPrint="Relatório de Margem"
      filterTablePrint={<FilterTablePrint />}
      landscape={true}
    >
      <FillinList />
    </ReportList>
  );
};

export default MarginReport;
